// INVLOC1 Slice 2 (docs/build-briefs/INVLOC1-S2-par-and-bin-label-per-bar.md section 3.8; DEC-326). Pure, no DOM (see
// test/ui-has-permission.test.ts's own header for why a plain node:test file can import a ui/src module directly): the
// logic the item editor's "How much we keep" section and the Stock levels list's location header trust. What shape the
// editor takes for the locations a person may plan for, the words on it, what a Save sends, and how the Stock list names its
// location, hides or shows its switch, and tells "no par" from "OK". The screens themselves are in ui/src/admin.ts.
//
// The approved screens are design/mockups/invloc1-s2-*.html: mockup 1 (one location), 2a (a table of locations, the tablet's location
// first, one Save), 3 (a location with nothing set, with a disabled "Copy pars from" marked coming later) and 4a and 4b
// (the Stock list header, the switch, "No par set").

import { parseExactDecimalToSmallestUnit } from "./money.ts";

/** One location's par and bin for one item, as `GET .../liquor-products/:id/stock-settings` returns it. */
export interface LocationSettingsEntry {
  stopOrdering?: boolean;
  locationId: string;
  locationName: string;
  /** True for the location of this tablet. The server puts it first. */
  isThisTablet: boolean;
  parLevel: string | null;
  binLabel: string | null;
}

/** What is typed in one location's two fields, as text. */
export interface TypedLocationValues {
  stopOrdering?: boolean;
  locationId: string;
  par: string;
  bin: string;
}

/** "none": the person may plan for no location (a session with no tablet), so no par or bin can be set. "single": one location,
 * the screen nearly as it was, with "at <location>" labels. "table": one row per location, one Save. */
export type EditorLayout = "none" | "single" | "table";

export function editorLayout(locations: readonly { locationId: string }[]): EditorLayout {
  if (locations.length === 0) return "none";
  return locations.length === 1 ? "single" : "table";
}

/** Mockup 1: the one-location wording. */
export function singleLocationCopy(locationName: string): {
  heading: string;
  intro: string;
  binLabel: string;
  parLabel: string;
} {
  return {
    heading: `At ${locationName}`,
    intro: `This is for ${locationName}.`,
    binLabel: `Bin at ${locationName}`,
    parLabel: `Par at ${locationName}`,
  };
}

/** Mockup 2a: the table's wording. `unitWord` is the plural count unit ("bottles") when one resolves. */
export function tableCopy(unitWord: string | null): {
  heading: string;
  intro: string;
  locationHeader: string;
  parHeader: string;
  binHeader: string;
  parPlaceholder: string;
  binPlaceholder: string;
  thisTabletTag: string;
} {
  return {
    heading: "How much we keep",
    intro: "Set par for each of your locations.",
    locationHeader: "Location",
    parHeader: unitWord === null ? "Par" : `Par (${unitWord})`,
    binHeader: "Bin label",
    parPlaceholder: "No par set",
    binPlaceholder: "No bin label",
    thisTabletTag: "This device",
  };
}

/** No location to plan for: what the section says instead of two fields that would save nowhere. */
export const NO_LOCATION_COPY =
  "Par is set for each location. Sign in on a device at your location to set it.";

/** The plural word the Par header uses, from the count unit the existing hint resolves, or null. */
export function parUnitWord(
  countUnit: "unit" | "keg" | "bottle" | "can" | null | undefined,
): string | null {
  switch (countUnit) {
    case "bottle":
      return "bottles";
    case "can":
      return "cans";
    case "keg":
      return "kegs";
    case "unit":
      return "units";
    default:
      return null;
  }
}

/** Mockup 3's dashed note: THIS TABLET'S location holds neither a par nor a bin for this item while another location holds one (a
 * location that has just been set up, whose tablet is the first to look). Mockup 2a, the same two locations seen from the location that
 * has the values, carries no note. Built from what was LOADED (not what is being typed), so the note does not appear and
 * vanish under the person's fingers. Names the tablet's location and the location a later "Copy pars from" would read. Null when
 * there is nothing to say. */
export function emptyLocationNote(
  loaded: readonly LocationSettingsEntry[],
): { locationName: string; copyFromName: string } | null {
  const tablet = loaded.find((siteLocation) => siteLocation.isThisTablet);
  if (tablet === undefined || tablet.parLevel !== null || tablet.stopOrdering === true) return null;
  const source = loaded.find(
    (siteLocation) =>
      siteLocation.locationId !== tablet.locationId && siteLocation.parLevel !== null,
  );
  if (source === undefined) return null;
  return { locationName: tablet.locationName, copyFromName: source.locationName };
}

/** The note's two sentences, location named. The lead sentence says what is true of THIS item at that location (the mockup says
 * the location is new, which the screen cannot know). */
export function emptyLocationNoteCopy(note: { locationName: string; copyFromName: string }): {
  lead: string;
  body: string;
  copyButton: string;
  later: string;
} {
  return {
    lead: `${note.locationName} has no par for this item yet.`,
    body: "Set one here, or leave it empty. Without a par, an item never shows as Low.",
    copyButton: `Copy pars from ${note.copyFromName}`,
    later: "Coming later",
  };
}

export interface StockSettingEntryToSend {
  stopOrdering?: boolean;
  locationId: string;
  parLevel?: number | null;
  binLabel?: string | null;
}

export type StockSettingsPayload =
  { ok: true; entries: StockSettingEntryToSend[] } | { ok: false; message: string };

/** The same exact, rejecting decimal parser the editor already used for par: up to two decimal places, never rounded. */
function parseParText(raw: string, label: string): number {
  return parseExactDecimalToSmallestUnit(raw, 2, label, "0.01") / 100;
}

/**
 * What one Save sends for the locations: one entry per location whose typed values differ from what was loaded, carrying only the
 * value that changed (a blank par is "clear it" and sends null; an omitted key leaves the value as it is). A location left
 * alone sends nothing, so saving an item never rewrites a location the person did not touch. A par that does not parse is
 * refused in plain words naming the location when there is more than one. `showLocationNames` is true for the table.
 */
export function buildStockSettingsPayload(
  loaded: readonly LocationSettingsEntry[],
  typed: readonly TypedLocationValues[],
  showLocationNames: boolean,
  includeBin = true,
): StockSettingsPayload {
  const entries: StockSettingEntryToSend[] = [];
  for (const siteLocation of loaded) {
    const values = typed.find((t) => t.locationId === siteLocation.locationId);
    if (values === undefined) continue;
    const entry: StockSettingEntryToSend = { locationId: siteLocation.locationId };
    const stopped = values.stopOrdering ?? siteLocation.stopOrdering ?? false;
    if (stopped !== (siteLocation.stopOrdering ?? false)) entry.stopOrdering = stopped;
    const parText = stopped ? "" : values.par.trim();
    const label = showLocationNames ? `Par at ${siteLocation.locationName}` : "Par level";
    if (parText.length === 0) {
      if (siteLocation.parLevel !== null) entry.parLevel = null;
    } else {
      let parsed: number;
      try {
        parsed = parseParText(parText, label);
      } catch (error) {
        return {
          ok: false,
          message: error instanceof Error ? error.message : `${label} is not a valid number.`,
        };
      }
      if (siteLocation.parLevel === null || Number(siteLocation.parLevel) !== parsed)
        entry.parLevel = parsed;
    }
    if (includeBin) {
      const binText = values.bin.trim();
      if (binText.length === 0) {
        if (siteLocation.binLabel !== null) entry.binLabel = null;
      } else if (siteLocation.binLabel !== binText) {
        entry.binLabel = binText;
      }
    }
    if (
      entry.parLevel !== undefined ||
      entry.binLabel !== undefined ||
      entry.stopOrdering !== undefined
    )
      entries.push(entry);
  }
  return { ok: true, entries };
}

// ---------------------------------------------------------------------
// The Stock levels list (mockups 4a and 4b)
// ---------------------------------------------------------------------

/** The switch appears only when the person may plan for more than one location (DEC-326). */
export function locationSwitchVisible(locations: readonly { id: string }[]): boolean {
  return locations.length > 1;
}

/** An item with no par at the shown location. A missing field (an old payload) reads as no par, never as a par of zero. */
export function hasNoPar(item: { par_level?: string | null | undefined }): boolean {
  return item.par_level === null || item.par_level === undefined;
}

/** The "No par set" filter: the active items with no par at this location. Out and flagged items are still listed (no par
 * says nothing about stock), and a disabled item is not a candidate for a par. */
export function matchesNoParFilter(item: {
  active: boolean;
  par_level?: string | null | undefined;
}): boolean {
  return item.active && hasNoPar(item);
}

/** Mockup 4b's blue note: shown when the person plans for more than one location and most of the shown location's active items
 * have no par. "Most" is more than half, and a location with no active items says nothing. */
export function showNoParNote(
  locations: readonly { id: string }[],
  items: readonly { active: boolean; par_level?: string | null | undefined }[],
): boolean {
  if (!locationSwitchVisible(locations)) return false;
  const active = items.filter((item) => item.active);
  if (active.length === 0) return false;
  return active.filter(hasNoPar).length * 2 > active.length;
}

export function noParNoteCopy(locationName: string): { title: string; body: string } {
  return {
    title: `${locationName} has no pars for most items yet.`,
    body: `Items without a par never show as Low. Open an item to set its par for ${locationName}.`,
  };
}

/** What the Par column shows for an item at the shown location. `narrow` is true at tablet-portrait width, where the cell is
 * too tight for "No par set" and says "No par" (the status pill beside it says the same). */
export function parCellText(
  item: { par_level?: string | null | undefined },
  narrow = false,
): string {
  if (!hasNoPar(item)) return item.par_level as string;
  return narrow ? "No par" : "No par set";
}
